import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Star } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import Logo from '../components/Logo';

const PricingPage = () => {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/subscription-tiers/public`);
      setTiers(response.data);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (tier) => {
    const price = billingCycle === 'monthly' ? tier.monthly_price : tier.annual_price;
    return price.toFixed(0);
  };

  const getSavingsPercent = (tier) => {
    if (billingCycle === 'monthly') return null;
    const savings = ((tier.monthly_price - tier.annual_price) / tier.monthly_price * 100).toFixed(0);
    return savings > 0 ? savings : null;
  };

  const featuresList = {
    basic_membership_management: "Basic Membership Management",
    patient_portal: "Patient Portal",
    appointment_scheduling: "Appointment Scheduling",
    payment_processing: "Payment Processing",
    email_notifications: "Email Notifications",
    basic_reporting: "Basic Reporting",
    automated_reminders: "Automated Reminders (Email & SMS)",
    secure_messaging: "Secure Messaging",
    advanced_analytics: "Advanced Analytics Dashboard",
    custom_branding: "Custom Branding",
    multiple_membership_tiers: "Multiple Membership Tiers",
    medical_notes: "Medical Notes & History",
    payment_plans: "Payment Plans Support",
    csv_export: "CSV Export",
    multi_location: "Multi-Location Support",
    stripe_connect: "Stripe Connect Integration",
    wordpress_plugin: "WordPress Plugin",
    api_access: "API Access",
    white_label: "White Label Options",
    custom_domain: "Custom Domain",
    priority_support: "Priority Support",
    dedicated_manager: "Dedicated Account Manager"
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <Logo className="h-12 w-12" />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-900">MyMedMember</span>
                <span className="text-xs text-gray-500 -mt-0.5">by Clinicax Systems</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/login')}>
                Sign In
              </Button>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => navigate('/login')}>
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
          Simple, Transparent Pricing
        </h1>
        <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Choose the perfect plan for your practice. All plans include a 30-day free trial.
        </p>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center space-x-4 mb-12">
          <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
            Monthly
          </span>
          <button
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              billingCycle === 'annual' ? 'bg-teal-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span className={`text-sm font-medium ${billingCycle === 'annual' ? 'text-gray-900' : 'text-gray-500'}`}>
            Annual
          </span>
          {billingCycle === 'annual' && (
            <Badge className="bg-green-100 text-green-800">Save 20%</Badge>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier) => (
            <Card
              key={tier.id}
              className={`relative ${
                tier.is_popular
                  ? 'border-2 border-teal-500 shadow-xl scale-105'
                  : 'border border-gray-200 hover:shadow-lg'
              } transition-all duration-200`}
            >
              {tier.is_popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-teal-600 text-white px-4 py-1 flex items-center gap-1">
                    <Star className="h-3 w-3 fill-current" />
                    Most Popular
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl mb-2">{tier.name}</CardTitle>
                <CardDescription className="text-sm">{tier.description}</CardDescription>
                
                <div className="mt-6">
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-gray-900">${formatPrice(tier)}</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                  {getSavingsPercent(tier) && (
                    <p className="text-sm text-green-600 mt-2">Save {getSavingsPercent(tier)}% with annual</p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    {billingCycle === 'annual' ? 'Billed annually' : 'Billed monthly'}
                  </p>
                </div>
              </CardHeader>

              <CardContent className="pt-6">
                <Button
                  className={`w-full mb-6 ${
                    tier.is_popular
                      ? 'bg-teal-600 hover:bg-teal-700'
                      : 'bg-gray-900 hover:bg-gray-800'
                  }`}
                  onClick={() => navigate('/login')}
                >
                  Start Free Trial
                </Button>

                <div className="space-y-4 mb-6">
                  <div className="text-sm font-semibold text-gray-900 border-b pb-2">
                    Limits
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-gray-700">
                      <Check className="h-4 w-4 text-teal-600 mr-2" />
                      <span>
                        {tier.max_members === -1 ? 'Unlimited' : `Up to ${tier.max_members}`} members
                      </span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Check className="h-4 w-4 text-teal-600 mr-2" />
                      <span>
                        {tier.max_users === -1 ? 'Unlimited' : tier.max_users} users
                      </span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Check className="h-4 w-4 text-teal-600 mr-2" />
                      <span>{tier.max_storage_gb}GB storage</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Check className="h-4 w-4 text-teal-600 mr-2" />
                      <span>{(tier.transaction_fee_percentage * 100).toFixed(1)}% transaction fee</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="text-sm font-semibold text-gray-900 border-b pb-2">
                    Features
                  </div>
                  <div className="space-y-2 text-sm max-h-96 overflow-y-auto">
                    {Object.entries(tier.features)
                      .filter(([_, enabled]) => enabled)
                      .map(([key, _]) => (
                        <div key={key} className="flex items-start text-gray-700">
                          <Check className="h-4 w-4 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span>{featuresList[key] || key}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* FAQ or Additional Info */}
        <div className="text-center">
          <p className="text-gray-600 mb-4">
            All plans include a 30-day free trial. No credit card required.
          </p>
          <p className="text-sm text-gray-500">
            Need help choosing? <a href="mailto:support@mymedmember.com" className="text-teal-600 hover:underline">Contact us</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
